/**
 * Physics simulation for free fall with projectile motion
 * Demonstrates independence of horizontal and vertical motion
 */

import type { Ball, SimulationState, SimulationParams, Vec2, LaunchConfig } from '../types';

export class Simulation {
  private state: SimulationState;
  private params: SimulationParams;

  constructor(params: SimulationParams) {
    this.params = params;
    this.state = this.createInitialState();
  }

  private createInitialState(): SimulationState {
    const ballRadius = 0.3; // meters

    // Calculate left ball position at the end of the launcher barrel
    const leftBallX = this.params.leftTowerX + 3.0;

    // Right ball positioned near the inner edge of the right tower
    const rightBallX = this.params.rightTowerX - 3.0;

    return {
      leftBall: {
        position: { x: leftBallX, y: this.params.towerHeight },
        velocity: { x: 0, y: 0 },
        color: '#00cc00',
        radius: ballRadius,
        active: false,
        trajectory: [],
      },
      rightBall: {
        position: { x: rightBallX, y: this.params.towerHeight },
        velocity: { x: 0, y: 0 },
        color: '#ff2700',
        radius: ballRadius,
        active: false,
        trajectory: [],
      },
      time: 0,
      isRunning: false,
      hasCollided: false,
      allBallsLanded: false,
    };
  }

  public getState(): SimulationState {
    return this.state;
  }

  public getParams(): SimulationParams {
    return this.params;
  }

  public updateParams(params: Partial<SimulationParams>): void {
    this.params = { ...this.params, ...params };
  }


  public launch(): void {
    // Set initial velocities based on launch config
    const leftVel = this.calculateVelocity(this.params.leftLaunch);
    const rightVel = this.calculateVelocity(this.params.rightLaunch);

    this.state.leftBall.velocity = leftVel;
    this.state.rightBall.velocity = rightVel;
    this.state.leftBall.active = true;
    this.state.rightBall.active = true;
    this.state.isRunning = true;
    this.state.hasCollided = false;
    this.state.allBallsLanded = false;
    this.state.time = 0;

    // Clear trajectories and add starting positions
    this.state.leftBall.trajectory = [{ ...this.state.leftBall.position }];
    this.state.rightBall.trajectory = [{ ...this.state.rightBall.position }];
  }

  private calculateVelocity(config: LaunchConfig): Vec2 {
    const angleRad = (config.angle * Math.PI) / 180;
    return {
      x: config.velocity * Math.cos(angleRad),
      y: config.velocity * Math.sin(angleRad),
    };
  }

  public step(dt: number): void {
    if (!this.state.isRunning) return;

    this.state.time += dt;

    // Update left ball
    if (this.state.leftBall.active) {
      this.updateBall(this.state.leftBall, dt);
    }

    // Update right ball
    if (this.state.rightBall.active) {
      this.updateBall(this.state.rightBall, dt);
    }

    // Check for collision
    if (
      this.state.leftBall.active &&
      this.state.rightBall.active &&
      this.checkCollision(this.state.leftBall, this.state.rightBall)
    ) {
      this.state.hasCollided = true;
      this.state.isRunning = false;
    }

    // Check if all balls have landed
    if (
      !this.state.leftBall.active &&
      !this.state.rightBall.active &&
      !this.state.hasCollided
    ) {
      this.state.allBallsLanded = true;
      this.state.isRunning = false;
    }
  }

  private updateBall(ball: Ball, dt: number): void {
    // Apply gravity to vertical velocity
    ball.velocity.y -= this.params.gravity * dt;

    // Update position
    ball.position.x += ball.velocity.x * dt;
    ball.position.y += ball.velocity.y * dt;

    // Record position in trajectory
    ball.trajectory.push({ ...ball.position });

    // Check ground collision
    if (ball.position.y <= this.params.groundLevel + ball.radius) {
      ball.position.y = this.params.groundLevel + ball.radius;
      ball.active = false;
      // Add final position to trajectory
      ball.trajectory.push({ ...ball.position });
    }
  }

  private checkCollision(ball1: Ball, ball2: Ball): boolean {
    const dx = ball2.position.x - ball1.position.x;
    const dy = ball2.position.y - ball1.position.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    const minDistance = ball1.radius + ball2.radius;

    return distance < minDistance;
  }

  public reset(): void {
    this.state = this.createInitialState();
  }
}
